package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CalendarMonth
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerColors
import androidx.compose.material3.DatePickerDefaults
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.DatePickerState
import androidx.compose.material3.DateRangePicker
import androidx.compose.material3.DateRangePickerState
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.SelectableDates
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.util.appLanguage
import com.evoapps.evoshift.util.pickerCalendarLocale
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cancel
import evoshift.composeapp.generated.resources.date_pick_open
import evoshift.composeapp.generated.resources.ok
import kotlin.time.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource

/*
 * SH-166: one calendar for every date field. A date field's calendar icon opens the Material date
 * picker; a pair of fields for a period (EvoDateRangeField) opens the range picker and fills both.
 * They look like the roster's month calendar (RosterSkeletonScreen's MonthDatePicker): a cream
 * card, navy days, the chosen days navy, today and the period in gold. Their language and first
 * day of the week follow the app's language (pickerCalendarLocale).
 */

/** Test tags of the pickers. */
object DatePickerTags {
    const val DIALOG = "date_picker_dialog"
    const val RANGE_DIALOG = "date_range_picker_dialog"
}

/** "3 listopada 2026" / "3 November 2026": a date written out, under a date field. */
@Composable
fun longDate(date: LocalDate): String = "${dayAndMonth(date)} ${date.year}"

/** The cream of the roster's month calendar. */
private val PickerCream = Color(0xFFF7F2E9)

/** The picker's title (the field's label) above the chosen date. */
private val PickerTitle: Color get() = DeepNavy.copy(alpha = 0.7f)

/** The years a picker without limits shows (the years [parseDateEntry] takes). */
private val PICKER_YEARS = 1900..2199

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun evoDatePickerColors(): DatePickerColors = DatePickerDefaults.colors(
    containerColor = PickerCream,
    titleContentColor = PickerTitle,
    headlineContentColor = DeepNavy,
    weekdayContentColor = DeepNavy.copy(alpha = 0.7f),
    subheadContentColor = DeepNavy.copy(alpha = 0.7f),
    navigationContentColor = DeepNavy,
    yearContentColor = DeepNavy,
    disabledYearContentColor = DeepNavy.copy(alpha = 0.3f),
    currentYearContentColor = OnScreen.accent,
    selectedYearContentColor = Color.White,
    selectedYearContainerColor = DeepNavy,
    dayContentColor = DeepNavy,
    disabledDayContentColor = DeepNavy.copy(alpha = 0.3f),
    selectedDayContentColor = Color.White,
    selectedDayContainerColor = DeepNavy,
    todayContentColor = OnScreen.accent,
    todayDateBorderColor = OnScreen.accent,
    dayInSelectionRangeContentColor = DeepNavy,
    dayInSelectionRangeContainerColor = DarkGold.copy(alpha = 0.3f),
    dividerColor = DeepNavy.copy(alpha = 0.12f)
)

private fun LocalDate.utcMillis(): Long = atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()

private fun Long.utcDate(): LocalDate = Instant.fromEpochMilliseconds(this).toLocalDateTime(TimeZone.UTC).date

/** Days outside [allowed] are greyed out and cannot be picked. */
@OptIn(ExperimentalMaterial3Api::class)
private class AllowedDates(private val allowed: ClosedRange<LocalDate>?) : SelectableDates {
    override fun isSelectableDate(utcTimeMillis: Long): Boolean = allowed == null || utcTimeMillis.utcDate() in allowed
    override fun isSelectableYear(year: Int): Boolean = allowed == null || year in allowed.start.year..allowed.endInclusive.year
}

private fun yearsOf(allowed: ClosedRange<LocalDate>?): IntRange =
    allowed?.let { it.start.year..it.endInclusive.year } ?: PICKER_YEARS

/** A picked date inside [allowed] (and the picker's years), else null. */
private fun LocalDate?.within(allowed: ClosedRange<LocalDate>?): LocalDate? =
    this?.takeIf { it.year in yearsOf(allowed) && (allowed == null || it in allowed) }

/** The calendar of one date field: [initial] chosen (if any), only [allowed] days pickable. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EvoDatePickerDialog(
    initial: LocalDate?,
    title: String,
    onPick: (LocalDate) -> Unit,
    onDismiss: () -> Unit,
    allowed: ClosedRange<LocalDate>? = null
) {
    val language = appLanguage()
    val selected = initial.within(allowed)
    val shown = selected ?: AppClock.today().within(allowed) ?: allowed?.start
    val state: DatePickerState = remember(language) {
        DatePickerState(
            locale = pickerCalendarLocale(language),
            initialSelectedDateMillis = selected?.utcMillis(),
            initialDisplayedMonthMillis = shown?.utcMillis(),
            yearRange = yearsOf(allowed),
            selectableDates = AllowedDates(allowed)
        )
    }
    val colors = evoDatePickerColors()
    ProvideScreenColors {
    DatePickerDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.testTag(DatePickerTags.DIALOG),
        confirmButton = {
            val millis = state.selectedDateMillis
            TextButton(onClick = { millis?.let { onPick(it.utcDate()) } }, enabled = millis != null) {
                Text(stringResource(Res.string.ok), color = DeepNavy.copy(alpha = if (millis != null) 1f else 0.4f), fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel), color = DeepNavy) }
        },
        colors = colors
    ) {
        DatePicker(
            state = state,
            title = { Text(title, Modifier.padding(start = 24.dp, end = 12.dp, top = 16.dp), color = PickerTitle) },
            showModeToggle = false,
            colors = colors
        )
    }
    }
}

/**
 * The calendar of a period: the first tap picks the start, the second the end (a day before the
 * start starts again from it). OK with only a start picks that one day.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EvoDateRangePickerDialog(
    start: LocalDate?,
    end: LocalDate?,
    title: String,
    onPick: (LocalDate, LocalDate) -> Unit,
    onDismiss: () -> Unit,
    allowed: ClosedRange<LocalDate>? = null
) {
    val language = appLanguage()
    val first = start.within(allowed)
    val last = end.within(allowed)?.takeIf { first != null && it >= first }
    val shown = first ?: AppClock.today().within(allowed) ?: allowed?.start
    val state: DateRangePickerState = remember(language) {
        androidx.compose.material3.DateRangePickerState(
            locale = pickerCalendarLocale(language),
            initialSelectedStartDateMillis = first?.utcMillis(),
            initialSelectedEndDateMillis = last?.utcMillis(),
            initialDisplayedMonthMillis = shown?.utcMillis(),
            yearRange = yearsOf(allowed),
            selectableDates = AllowedDates(allowed)
        )
    }
    val colors = evoDatePickerColors()
    ProvideScreenColors {
    DatePickerDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.testTag(DatePickerTags.RANGE_DIALOG),
        confirmButton = {
            val from = state.selectedStartDateMillis
            TextButton(
                onClick = { from?.let { onPick(it.utcDate(), (state.selectedEndDateMillis ?: it).utcDate()) } },
                enabled = from != null
            ) {
                Text(stringResource(Res.string.ok), color = DeepNavy.copy(alpha = if (from != null) 1f else 0.4f), fontWeight = FontWeight.Bold)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel), color = DeepNavy) }
        },
        colors = colors
    ) {
        DateRangePicker(
            state = state,
            // In a dialog (not full screen) the title needs the top margin the date picker's has.
            title = { Text(title, Modifier.padding(start = 64.dp, end = 12.dp, top = 16.dp), color = PickerTitle) },
            showModeToggle = false,
            colors = colors
        )
    }
    }
}

/** The calendar icon at the end of a date field. */
@Composable
internal fun CalendarIconButton(onClick: () -> Unit, enabled: Boolean = true) {
    IconButtonWithTooltip(onClick = onClick, enabled = enabled, tooltip = stringResource(Res.string.date_pick_open)) {
        Icon(Icons.Default.CalendarMonth, contentDescription = stringResource(Res.string.date_pick_open))
    }
}

/**
 * SH-166: a period as two date fields (start and end), typed as in [EvoDateField] or picked
 * together in the range picker from either field's calendar icon. Each value reaches the caller
 * as soon as it is a whole date, so what depends on the period (e.g. the leave balance) follows
 * while typing. [allowed] limits the picker's days (e.g. one month); the caller still checks
 * typed dates against it and passes its messages as [startError] / [endError].
 */
@Composable
fun EvoDateRangeField(
    start: String,
    end: String,
    onStartChange: (String) -> Unit,
    onEndChange: (String) -> Unit,
    startLabel: String,
    endLabel: String,
    modifier: Modifier = Modifier,
    pickerTitle: String? = null,
    allowed: ClosedRange<LocalDate>? = null,
    missingYear: MissingYear = MissingYear.ThisYear,
    showErrors: Boolean = false,
    startError: String? = null,
    endError: String? = null,
    imeAction: ImeAction = ImeAction.Next
) {
    var picking by rememberSaveable { mutableStateOf(false) }
    val today = AppClock.today()
    val startDate = normalisedDate(start, today, missingYear)
    val endDate = normalisedDate(end, today, missingYear)
    val openPicker = { picking = true }
    @Composable
    fun StartField(fieldModifier: Modifier) = EvoDateField(
        value = start, onValueChange = onStartChange, label = startLabel, modifier = fieldModifier,
        showErrors = showErrors, error = startError, missingYear = missingYear, onOpenPicker = openPicker
    )
    @Composable
    fun EndField(fieldModifier: Modifier) = EvoDateField(
        value = end, onValueChange = onEndChange, label = endLabel, modifier = fieldModifier,
        showErrors = showErrors, error = endError, missingYear = missingYear, onOpenPicker = openPicker, imeAction = imeAction
    )
    // Side by side where both dates fit; one under the other on a narrow phone.
    BoxWithConstraints(modifier) {
        if (maxWidth >= RANGE_SIDE_BY_SIDE_MIN_WIDTH) {
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                StartField(Modifier.weight(1f))
                EndField(Modifier.weight(1f))
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                StartField(Modifier)
                EndField(Modifier)
            }
        }
    }
    if (picking) {
        EvoDateRangePickerDialog(
            start = startDate,
            end = endDate,
            title = pickerTitle ?: "$startLabel – $endLabel",
            allowed = allowed,
            onPick = { from, to ->
                onStartChange(from.toString())
                onEndChange(to.toString())
                picking = false
            },
            onDismiss = { picking = false }
        )
    }
}

/** Narrower than this, the two fields of a period go one under the other. */
private val RANGE_SIDE_BY_SIDE_MIN_WIDTH = 340.dp
